import React, { useState, useRef } from 'react';
import './App.css';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiFolder, FiFolderPlus, FiHardDrive, FiDownload, FiCheck, FiAlertCircle, FiLoader, FiRotateCcw } = FiIcons;

function App() {
  const [sourceType, setSourceType] = useState('folder'); // 'drive' or 'folder'
  const [selectedPath, setSelectedPath] = useState('');
  const [scannedFiles, setScannedFiles] = useState([]);
  const [outputTree, setOutputTree] = useState(true);
  const [outputPaths, setOutputPaths] = useState(true);
  const [outputFolder, setOutputFolder] = useState('');
  const [status, setStatus] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [results, setResults] = useState([]);
  const [drives] = useState(['C:', 'D:', 'E:', 'F:', 'G:']); // Beispiel-Laufwerke
  
  const folderInputRef = useRef();
  const outputFolderInputRef = useRef();

  const handleReset = () => {
    setSourceType('folder');
    setSelectedPath('');
    setScannedFiles([]);
    setOutputTree(true);
    setOutputPaths(true);
    setOutputFolder('');
    setStatus('');
    setResults([]);
    setIsProcessing(false);
  };

  const scanDirectory = async (dirHandle, path, results) => {
    for await (const entry of dirHandle.values()) {
      const entryPath = path ? `${path}/${entry.name}` : entry.name;
      results.push(entryPath);
      if (entry.kind === 'directory') {
        await scanDirectory(entry, entryPath, results);
      }
    }
  };

  const scanEntry = async (entry, path, results) => {
    const fullPath = path ? `${path}/${entry.name}` : entry.name;
    results.push(fullPath);

    if (entry.isDirectory) {
      const reader = entry.createReader();
      const readAllEntries = async () => {
        let allEntries = [];
        let batch = await new Promise(resolve => reader.readEntries(resolve));
        while (batch.length > 0) {
          allEntries.push(...batch);
          batch = await new Promise(resolve => reader.readEntries(resolve));
        }
        return allEntries;
      };

      const entries = await readAllEntries();
      for (const child of entries) {
        await scanEntry(child, fullPath, results);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.items && e.dataTransfer.items[0]) {
      const item = e.dataTransfer.items[0].webkitGetAsEntry();
      if (item) {
        setSelectedPath(item.name);
        setStatus('Scanne...');
        const files = [];
        if (item.isDirectory) {
          setSourceType('folder');
          // Start scanning from the contents, so we use empty string as initial path
          const reader = item.createReader();
          const entries = await new Promise(resolve => reader.readEntries(resolve));
          for (const child of entries) {
            await scanEntry(child, '', files);
          }
        } else {
          files.push(item.name);
        }
        setScannedFiles(files);
        setStatus('');
      }
    }
  };

  const handleFolderSelect = async () => {
    if ('showDirectoryPicker' in window) {
      try {
        const handle = await window.showDirectoryPicker();
        setSelectedPath(handle.name);
        setStatus('Scanne Ordner...');
        const files = [];
        await scanDirectory(handle, '', files);
        setScannedFiles(files);
        setStatus('');
        setSourceType('folder');
      } catch (err) {
        if (err.name !== 'AbortError') {
          setStatus(`Fehler beim Zugriff: ${err.message}`);
        }
      }
    } else if (folderInputRef.current) {
      folderInputRef.current.click();
    }
  };

  const handleOutputFolderSelect = () => {
    if (outputFolderInputRef.current) {
      outputFolderInputRef.current.click();
    }
  };

  const handleFolderChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const root = files[0].webkitRelativePath.split('/')[0];
      setSelectedPath(root);
      const paths = files.map(f => f.webkitRelativePath.split('/').slice(1).join('/'));
      setScannedFiles(paths);
      setStatus('');
    }
  };

  const generateFileName = (type, rootName) => {
    const now = new Date();
    const date = now.toISOString().split('T')[0]; // YYYY-MM-DD
    const time = now.toTimeString().split(' ')[0].replace(/:/g, ''); // HHMMSS
    const cleanName = rootName.replace(/[:\\]/g, '-');
    return `${cleanName}-${type}-${date}-${time}.txt`;
  };

  const generateTreeStructure = (rootName, filePaths) => {
    const header = `Baumstruktur für: ${rootName}\nErstellt am: ${new Date().toLocaleString()}\nOptionen: Baumstruktur\n\n`;
    
    if (filePaths.length === 0) return header + rootName + "\n(Keine Dateien gefunden)";

    const tree = {};
    filePaths.forEach(path => {
      const parts = path.split('/');
      let current = tree;
      parts.forEach(part => {
        if (!current[part]) current[part] = {};
        current = current[part];
      });
    });

    const buildTreeString = (node, prefix = '') => {
      let result = '';
      const keys = Object.keys(node).sort((a, b) => a.localeCompare(b));
      keys.forEach((key, index) => {
        const isLast = index === keys.length - 1;
        result += `${prefix}${isLast ? '└─ ' : '├─ '}${key}\n`;
        result += buildTreeString(node[key], `${prefix}${isLast ? '   ' : '│  '}`);
      });
      return result;
    };

    return header + rootName + '\n' + buildTreeString(tree);
  };

  const generatePathStructure = (rootName, filePaths) => {
    const header = `Pfadstruktur für: ${rootName}\nErstellt am: ${new Date().toLocaleString()}\nOptionen: Pfadstruktur\n\n`;
    if (filePaths.length === 0) return header + "(Keine Dateien gefunden)";
    const paths = filePaths.map(p => `${rootName}\\${p.replace(/\//g, '\\')}`).join('\n');
    return header + paths;
  };

  const downloadFile = (content, filename) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleGenerate = async () => {
    if (!outputTree && !outputPaths) {
      setStatus('Bitte mindestens eine Ausgabe wählen');
      return;
    }

    if (!selectedPath && sourceType === 'folder') {
      setStatus('Bitte einen Ordner auswählen');
      return;
    }

    setIsProcessing(true);
    setStatus('Generiere Dateien...');
    setResults([]);

    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      const rootName = selectedPath || (sourceType === 'drive' ? 'Laufwerk' : 'Ordner');
      const data = scannedFiles;
      const generatedFiles = [];

      if (outputTree) {
        const treeContent = generateTreeStructure(rootName, data);
        const treeFileName = generateFileName('Baumstruktur', rootName);
        downloadFile(treeContent, treeFileName);
        generatedFiles.push({ type: 'Baumstruktur', name: treeFileName });
      }

      if (outputPaths) {
        const pathsContent = generatePathStructure(rootName, data);
        const pathsFileName = generateFileName('Pfadstruktur', rootName);
        downloadFile(pathsContent, pathsFileName);
        generatedFiles.push({ type: 'Pfadstruktur', name: pathsFileName });
      }

      setResults(generatedFiles);
      setStatus('Erfolgreich erstellt!');
    } catch (error) {
      setStatus(`Fehler: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="app-container">
      <div className="app-content">
        <header className="app-header">
          <img 
            src="https://meinerfahrungsbericht.de/logo-me/me-logo.png" 
            alt="ME Logo" 
            className="logo"
          />
          <h1 className="app-title">Baum- & Pfadstruktur Generator</h1>
        </header>

        <div className="main-content">
          <div className="section">
            <h2 className="section-title">Quelle wählen</h2>
            
            <div className="source-selector">
              <label className="radio-option">
                <input
                  type="radio"
                  value="drive"
                  checked={sourceType === 'drive'}
                  onChange={(e) => setSourceType(e.target.value)}
                />
                <SafeIcon icon={FiHardDrive} className="radio-icon" />
                Laufwerk
              </label>
              
              <label className="radio-option">
                <input
                  type="radio"
                  value="folder"
                  checked={sourceType === 'folder'}
                  onChange={(e) => setSourceType(e.target.value)}
                />
                <SafeIcon icon={FiFolder} className="radio-icon" />
                Ordner
              </label>
            </div>

            <div 
              className={`dropzone ${isDragging ? 'is-dragover' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="dropzone-content">
                <p className="dropzone-text">{isDragging ? 'Jetzt loslassen…' : 'Ordner oder Laufwerk hierher ziehen'}</p>
                <p className="dropzone-subtext">Alternativ: 'Ordner wählen…' nutzen</p>
              </div>
            </div>

            {sourceType === 'drive' && (
              <div className="drive-selector">
                <select
                  value={selectedPath}
                  onChange={(e) => { setSelectedPath(e.target.value); setStatus(''); setScannedFiles([]); }}
                  className="drive-dropdown"
                >
                  <option value="">Laufwerk wählen...</option>
                  {drives.map(drive => (
                    <option key={drive} value={drive}>{drive}</option>
                  ))}
                </select>
              </div>
            )}

            {sourceType === 'folder' && (
              <div className="folder-selector">
                <button onClick={handleFolderSelect} className="folder-button">
                  <SafeIcon icon={FiFolderPlus} className="button-icon" />
                  Ordner wählen...
                </button>
                <input
                  ref={folderInputRef}
                  type="file"
                  webkitdirectory=""
                  directory=""
                  multiple
                  onChange={handleFolderChange}
                  style={{ display: 'none' }}
                />
                {selectedPath && (
                  <div className="selected-path">Ausgewählt: {selectedPath} ({scannedFiles.length} Elemente)</div>
                )}
              </div>
            )}
          </div>

          <div className="section">
            <h2 className="section-title">Ausgabe-Optionen</h2>
            
            <div className="checkbox-group">
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={outputTree}
                  onChange={(e) => setOutputTree(e.target.checked)}
                />
                <span className="checkbox-label">Baumstruktur erstellen</span>
              </label>
              
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={outputPaths}
                  onChange={(e) => setOutputPaths(e.target.checked)}
                />
                <span className="checkbox-label">Pfadstruktur erstellen</span>
              </label>
            </div>
          </div>

          <div className="section">
            <h2 className="section-title">Ausgabeordner (optional)</h2>
            
            <div className="output-folder">
              <button onClick={handleOutputFolderSelect} className="folder-button secondary">
                <SafeIcon icon={FiFolder} className="button-icon" />
                Ausgabeordner wählen...
              </button>
              <input
                ref={outputFolderInputRef}
                type="file"
                webkitdirectory=""
                directory=""
                onChange={(e) => setOutputFolder(e.target.files[0]?.webkitRelativePath.split('/')[0] || '')}
                style={{ display: 'none' }}
              />
              {outputFolder && (
                <div className="selected-path">{outputFolder}</div>
              )}
              <div className="hint">Standard: Downloads-Ordner</div>
            </div>
          </div>

          <div className="action-section">
            <button 
              onClick={handleGenerate}
              disabled={isProcessing}
              className="generate-button"
            >
              {isProcessing ? (
                <>
                  <SafeIcon icon={FiLoader} className="button-icon spinning" />
                  Erstelle...
                </>
              ) : (
                <>
                  <SafeIcon icon={FiDownload} className="button-icon" />
                  Erstellen
                </>
              )}
            </button>
            
            <button 
              onClick={handleReset}
              className="folder-button secondary reset-button"
              disabled={isProcessing}
            >
              <SafeIcon icon={FiRotateCcw} className="button-icon" />
              Zurücksetzen
            </button>
          </div>

          {status && (
            <div className={`status ${isProcessing ? 'processing' : results.length > 0 ? 'success' : 'error'}`}>
              <SafeIcon 
                icon={isProcessing ? FiLoader : results.length > 0 ? FiCheck : FiAlertCircle} 
                className={`status-icon ${isProcessing ? 'spinning' : ''}`}
              />
              {status}
            </div>
          )}

          {results.length > 0 && (
            <div className="results-section">
              <h3 className="results-title">Erstellte Dateien:</h3>
              <div className="results-list">
                {results.map((result, index) => (
                  <div key={index} className="result-item">
                    <span className="result-type">{result.type}:</span>
                    <span className="result-name">{result.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;